import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowUpRight, HelpCircle, BadgeIndianRupee, PlugZap, ShieldCheck } from "lucide-react";
import Reveal from "@/components/Reveal";

const categories = [
  {
    id: "general", icon: HelpCircle, title: "General Solar Questions",
    faqs: [
      {
        q: "How much can I actually save with rooftop solar?",
        a: "Most homes cut their electricity bill by 80–95%. A ₹5,000 monthly bill typically drops to ₹300–500. The system pays for itself in 3–4 years and then generates effectively free power for 20+ more years.",
      },
      {
        q: "Does solar work during monsoon or cloudy days?",
        a: "Yes. Panels generate 25–45% of their rated output even on overcast days. Your system is sized using annual average sunlight, so cloudy months are already accounted for in the savings estimate.",
      },
      {
        q: "What happens to solar power during a power cut?",
        a: "Standard on-grid systems shut down during outages for grid-worker safety. If you need backup, we can design a hybrid system with batteries — ask us during your free survey.",
      },
    ],
  },
  {
    id: "subsidy", icon: BadgeIndianRupee, title: "PM Surya Ghar Subsidy",
    faqs: [
      {
        q: "How much subsidy can I get under PM Surya Ghar Muft Bijli Yojana?",
        a: "₹30,000 for a 1 kW system, ₹60,000 for 2 kW, and ₹78,000 for 3 kW and above (capped). The subsidy is credited directly to your bank account by the government.",
      },
      {
        q: "Who is eligible for the subsidy?",
        a: "Any Indian household with a valid electricity connection and rooftop rights, installing an on-grid system through a registered vendor with DCR (domestic content) panels. SolarSoch handles the complete eligibility check and filing for you.",
      },
      {
        q: "How long does the subsidy take to arrive?",
        a: "Typically 30–60 days after net meter commissioning, depending on portal processing. We track your application end-to-end and keep you updated at every stage.",
      },
    ],
  },
  {
    id: "netmetering", icon: PlugZap, title: "Net Metering",
    faqs: [
      {
        q: "What is net metering?",
        a: "A bi-directional meter that records both the power you draw from the grid and the surplus solar power you export. Exported units offset your bill — you pay only for the 'net' consumption.",
      },
      {
        q: "Do I have to deal with BESCOM / DISCOM myself?",
        a: "No. We handle the entire DISCOM process — application, feasibility approval, inspection, agreement signing and meter commissioning. You won't need to visit any office.",
      },
      {
        q: "How long does net meter installation take?",
        a: "Usually 15–45 days after installation, depending on DISCOM workload. Our liaison team follows up daily to keep it moving.",
      },
    ],
  },
  {
    id: "warranty", icon: ShieldCheck, title: "Installation & Warranty",
    faqs: [
      {
        q: "How long does installation take and will my roof be damaged?",
        a: "Most homes are completed in 1–2 days. We use leak-proof elevated structures with chemical anchoring and waterproofing — your roof stays protected, and panels can even reduce terrace heat.",
      },
      {
        q: "What warranties do I get?",
        a: "Panels carry a 12-year product warranty and 25–30 year performance warranty. Inverters carry 5–10 years. On top of this, SolarSoch provides structured maintenance support for the life of the plant.",
      },
      {
        q: "What maintenance does a solar plant need?",
        a: "Very little — mainly panel cleaning every 2–4 weeks (dust can cut output by 25%) and an annual electrical health check. Our AMC plans cover all of it, so you never climb the roof.",
      },
    ],
  },
];

export default function Faq() {
  return (
    <div data-testid="faq-page">
      <section className="relative bg-brand-deep text-white grain overflow-hidden pt-32 pb-20">
        <div className="absolute -top-32 right-0 w-[26rem] h-[26rem] rounded-full bg-gold/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs tracking-[0.35em] text-gold mb-4"
          >
            FREQUENTLY ASKED
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
            >
              Every doubt, <span className="text-gold">answered straight.</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-6 max-w-2xl text-white/70 text-base md:text-lg"
          >
            The questions every homeowner asks before going solar — with honest numbers, not sales talk.
          </motion.p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-20 space-y-14">
        {categories.map((cat, ci) => (
          <Reveal key={cat.id} delay={ci * 0.05}>
            <div data-testid={`faq-category-${cat.id}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-2xl bg-brand flex items-center justify-center">
                  <cat.icon size={20} className="text-gold" />
                </div>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-ink">{cat.title}</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {cat.faqs.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`${cat.id}-${i}`}
                    data-testid={`faq-accordion-item-${cat.id}-${i}`}
                    className="rounded-2xl bg-white border border-brand/10 px-6 transition-colors data-[state=open]:border-gold data-[state=open]:shadow-[0_12px_40px_rgba(3,59,43,0.08)]"
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${cat.id}-${i}`}
                      className="text-left font-display font-bold text-base md:text-lg text-ink hover:no-underline hover:text-brand py-5"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-moss leading-relaxed pb-6 text-sm md:text-base">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        ))}

        <Reveal>
          <div className="rounded-[2rem] bg-brand-deep text-white p-8 md:p-12 text-center grain relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="font-display font-extrabold text-3xl md:text-4xl">Still curious?</h3>
              <p className="mt-3 text-white/70 max-w-md mx-auto">
                Talk to a solar engineer — not a salesperson. Five honest minutes can save you lakhs.
              </p>
              <Link
                to="/contact"
                data-testid="faq-cta-contact"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold text-brand-deep px-8 py-4 font-bold hover:scale-105 transition-transform"
              >
                Ask Us Anything <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
